import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { sendNotifications } from '../../services/emailService';

interface Solicitud {
  id: string;
  fecha: string;
  tipoPerfil: string;
  urgencia: string;
  estado: 'Pendiente' | 'En revisión' | 'Completado';
}

export default function EmpresaPage() {
  const { user, isAuthenticated, authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'solicitudes' | 'nueva' | 'proceso'>('solicitudes');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    tipoPerfil: '',
    sector: '',
    experiencia: '',
    pais: '',
    urgencia: 'media',
    detalles: '',
    confidencialidad: true
  });

  // Mock solicitudes
  const [solicitudes] = useState<Solicitud[]>([]);

  useEffect(() => {
    // Esperar a que termine de verificar la sesión
    if (authLoading) return;

    if (!isAuthenticated || user?.userType !== 'empresa') {
      navigate('/');
    }
  }, [isAuthenticated, user, navigate, authLoading]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // Enviar notificaciones por email (admin + empresa)
      await sendNotifications(
        'Solicitud de Reunión Estratégica',
        {
          nombre: user?.nombreEmpresa || 'Empresa',
          email: user?.email || '',
          tipoPerfil: formData.tipoPerfil,
          sector: formData.sector,
          experienciaRequerida: formData.experiencia,
          paisDestino: formData.pais,
          urgencia: formData.urgencia,
          detalles: formData.detalles || 'No especificado',
          fecha: new Date().toLocaleString('es-ES', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })
        },
        user?.email || '',
        user?.nombreEmpresa || 'Empresa'
      );

      setShowSuccessMessage(true);
      setFormData({
        tipoPerfil: '',
        sector: '',
        experiencia: '',
        pais: '',
        urgencia: 'media',
        detalles: '',
        confidencialidad: true
      });
      
      setTimeout(() => {
        setShowSuccessMessage(false);
        setActiveTab('solicitudes');
      }, 3000);
    } catch (error) {
      console.error('Error al enviar solicitud:', error);
      // Mostrar mensaje de éxito de todas formas para no bloquear al usuario
      setShowSuccessMessage(true);
      setFormData({
        tipoPerfil: '',
        sector: '',
        experiencia: '',
        pais: '',
        urgencia: 'media',
        detalles: '',
        confidencialidad: true
      });
      
      setTimeout(() => {
        setShowSuccessMessage(false);
        setActiveTab('solicitudes');
      }, 3000);
    }
  };

  // Mostrar spinner mientras verifica la sesión
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#1A2B4A] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Verificando sesión...</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Confidentiality Banner */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-100">
        <div className="max-w-7xl mx-auto px-8 py-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="ri-shield-check-line text-2xl text-green-700"></i>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Confidencialidad garantizada</h3>
              <p className="text-gray-700 leading-relaxed">
                Todas las solicitudes y procesos de selección se gestionan bajo estricta confidencialidad. 
                La información compartida está protegida y solo es accesible para el equipo de BN ĀGENZIA 
                asignado a su proceso. Garantizamos discreción absoluta en cada fase del reclutamiento.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-8">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab('solicitudes')}
              className={`px-6 py-4 font-medium text-sm transition-all relative whitespace-nowrap ${
                activeTab === 'solicitudes'
                  ? 'text-[#1A2B4A] border-b-2 border-[#1A2B4A]'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="ri-file-list-3-line mr-2"></i>
              Gestión de solicitudes
            </button>
            <button
              onClick={() => setActiveTab('nueva')}
              className={`px-6 py-4 font-medium text-sm transition-all relative whitespace-nowrap ${
                activeTab === 'nueva'
                  ? 'text-[#1A2B4A] border-b-2 border-[#1A2B4A]'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="ri-add-circle-line mr-2"></i>
              Solicitar reunión estratégica
            </button>
            <button
              onClick={() => setActiveTab('proceso')}
              className={`px-6 py-4 font-medium text-sm transition-all relative whitespace-nowrap ${
                activeTab === 'proceso'
                  ? 'text-[#1A2B4A] border-b-2 border-[#1A2B4A]'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="ri-flow-chart mr-2"></i>
              Información del proceso
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-8 py-12">
        {/* Gestión de solicitudes */}
        {activeTab === 'solicitudes' && (
          <div>
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Solicitudes enviadas</h2>
              <p className="text-gray-600">Seguimiento de sus procesos de selección activos</p>
            </div>

            {solicitudes.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-16 text-center">
                <div className="w-20 h-20 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <i className="ri-inbox-line text-4xl text-gray-400"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">No hay solicitudes activas</h3>
                <p className="text-gray-600 mb-8 max-w-md mx-auto leading-relaxed">
                  Aún no ha enviado ninguna solicitud de búsqueda de talento. Cuando solicite una reunión estratégica, 
                  podrá hacer seguimiento del proceso desde aquí.
                </p>
                <button
                  onClick={() => setActiveTab('nueva')}
                  className="inline-flex items-center gap-2 px-8 py-3 bg-[#1A2B4A] text-white font-medium rounded-lg hover:bg-[#2A3B5A] transition-colors whitespace-nowrap"
                >
                  <i className="ri-add-line text-lg"></i>
                  Solicitar reunión estratégica
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {solicitudes.map((solicitud) => (
                  <div key={solicitud.id} className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-bold text-gray-900">{solicitud.tipoPerfil}</h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                            solicitud.estado === 'Completado' ? 'bg-green-100 text-green-700' :
                            solicitud.estado === 'En revisión' ? 'bg-blue-100 text-blue-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {solicitud.estado}
                          </span>
                        </div>
                        <div className="flex items-center gap-6 text-sm text-gray-600">
                          <span className="flex items-center gap-2">
                            <i className="ri-calendar-line"></i>
                            {solicitud.fecha}
                          </span>
                          <span className="flex items-center gap-2">
                            <i className="ri-time-line"></i>
                            Urgencia: {solicitud.urgencia}
                          </span>
                        </div>
                      </div>
                      <button className="px-6 py-2.5 text-[#1A2B4A] font-medium rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap">
                        Ver detalles
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Solicitar reunión estratégica */}
        {activeTab === 'nueva' && (
          <div>
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Solicitar reunión estratégica</h2>
              <p className="text-gray-600">Complete el formulario para iniciar un proceso de búsqueda de talento</p>
            </div>

            {showSuccessMessage && (
              <div className="mb-8 bg-green-50 border border-green-200 rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-checkbox-circle-line text-2xl text-green-600"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-green-900 mb-2">Solicitud enviada correctamente</h3>
                    <p className="text-green-700">
                      Nuestro equipo revisará su solicitud y se pondrá en contacto en un plazo máximo de 48 horas 
                      para coordinar la reunión estratégica.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-gray-200 p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Tipo de perfil buscado <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.tipoPerfil}
                    onChange={(e) => setFormData({ ...formData, tipoPerfil: e.target.value })}
                    placeholder="Ej: Ingeniero de Software Senior"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A2B4A] focus:border-transparent text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Sector <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    value={formData.sector}
                    onChange={(e) => setFormData({ ...formData, sector: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A2B4A] focus:border-transparent text-sm"
                  >
                    <option value="">Seleccionar sector</option>
                    <option value="Tecnología">Tecnología</option>
                    <option value="Ingeniería">Ingeniería</option>
                    <option value="Salud">Salud</option>
                    <option value="Finanzas">Finanzas</option>
                    <option value="Manufactura">Manufactura</option>
                    <option value="Logística">Logística</option>
                    <option value="Hostelería">Hostelería</option>
                    <option value="Otro">Otro</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Experiencia requerida <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    value={formData.experiencia}
                    onChange={(e) => setFormData({ ...formData, experiencia: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A2B4A] focus:border-transparent text-sm"
                  >
                    <option value="">Seleccionar experiencia</option>
                    <option value="Junior (0-2 años)">Junior (0-2 años)</option>
                    <option value="Mid-level (3-5 años)">Mid-level (3-5 años)</option>
                    <option value="Senior (6-10 años)">Senior (6-10 años)</option>
                    <option value="Expert (10+ años)">Expert (10+ años)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    País de destino <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    value={formData.pais}
                    onChange={(e) => setFormData({ ...formData, pais: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A2B4A] focus:border-transparent text-sm"
                  >
                    <option value="">Seleccionar país</option>
                    <option value="Alemania">Alemania</option>
                    <option value="Suiza">Suiza</option>
                    <option value="Países Bajos">Países Bajos</option>
                    <option value="Austria">Austria</option>
                    <option value="Bélgica">Bélgica</option>
                  </select>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Nivel de urgencia <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-3 gap-4">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, urgencia: 'baja' })}
                    className={`px-6 py-4 rounded-lg border-2 transition-all whitespace-nowrap ${
                      formData.urgencia === 'baja'
                        ? 'border-[#1A2B4A] bg-[#1A2B4A]/5 text-[#1A2B4A] font-semibold'
                        : 'border-gray-200 hover:border-gray-300 text-gray-700'
                    }`}
                  >
                    <div className="text-center">
                      <i className="ri-time-line text-2xl mb-2 block"></i>
                      <div className="font-semibold">Baja</div>
                      <div className="text-xs opacity-70">Más de 3 meses</div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, urgencia: 'media' })}
                    className={`px-6 py-4 rounded-lg border-2 transition-all whitespace-nowrap ${
                      formData.urgencia === 'media'
                        ? 'border-[#1A2B4A] bg-[#1A2B4A]/5 text-[#1A2B4A] font-semibold'
                        : 'border-gray-200 hover:border-gray-300 text-gray-700'
                    }`}
                  >
                    <div className="text-center">
                      <i className="ri-timer-line text-2xl mb-2 block"></i>
                      <div className="font-semibold">Media</div>
                      <div className="text-xs opacity-70">1-3 meses</div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, urgencia: 'alta' })}
                    className={`px-6 py-4 rounded-lg border-2 transition-all whitespace-nowrap ${
                      formData.urgencia === 'alta'
                        ? 'border-[#1A2B4A] bg-[#1A2B4A]/5 text-[#1A2B4A] font-semibold'
                        : 'border-gray-200 hover:border-gray-300 text-gray-700'
                    }`}
                  >
                    <div className="text-center">
                      <i className="ri-alarm-warning-line text-2xl mb-2 block"></i>
                      <div className="font-semibold">Alta</div>
                      <div className="text-xs opacity-70">Menos de 1 mes</div>
                    </div>
                  </button>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Detalles adicionales
                </label>
                <textarea
                  value={formData.detalles}
                  onChange={(e) => setFormData({ ...formData, detalles: e.target.value })}
                  placeholder="Describa requisitos específicos, competencias técnicas, idiomas necesarios, o cualquier información relevante para el proceso de selección..."
                  rows={6}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A2B4A] focus:border-transparent text-sm resize-none"
                />
                <p className="text-xs text-gray-500 mt-2">Máximo 500 caracteres</p>
              </div>

              <div className="mb-8">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.confidencialidad}
                    onChange={(e) => setFormData({ ...formData, confidencialidad: e.target.checked })}
                    className="mt-1 w-5 h-5 text-[#1A2B4A] border-gray-300 rounded focus:ring-[#1A2B4A]"
                  />
                  <span className="text-sm text-gray-700 leading-relaxed">
                    Confirmo que la información proporcionada es confidencial y autorizo a BN ĀGENZIA 
                    a iniciar el proceso de búsqueda de talento bajo los términos de confidencialidad establecidos.
                  </span>
                </label>
              </div>

              <div className="flex items-center justify-end gap-4">
                <button
                  type="button"
                  onClick={() => setActiveTab('solicitudes')}
                  className="px-8 py-3 text-gray-700 font-medium rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-8 py-3 bg-[#1A2B4A] text-white font-medium rounded-lg hover:bg-[#2A3B5A] transition-colors whitespace-nowrap"
                >
                  Enviar solicitud
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Información del proceso */}
        {activeTab === 'proceso' && (
          <div>
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Proceso de selección BN ĀGENZIA</h2>
              <p className="text-gray-600">Metodología estructurada en tres fases para garantizar la mejor selección</p>
            </div>

            <div className="space-y-6">
              {/* Fase 1 */}
              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-[#1A2B4A] to-[#2A3B5A] px-8 py-6">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center backdrop-blur-sm flex-shrink-0">
                      <span className="text-3xl font-bold text-white">01</span>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-white mb-1">Análisis estratégico</h3>
                      <p className="text-white/80">Definición precisa del perfil y requisitos</p>
                    </div>
                  </div>
                </div>
                <div className="p-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-[#1A2B4A]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-discuss-line text-[#1A2B4A] text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-2">Reunión estratégica inicial</h4>
                        <p className="text-gray-600 text-sm leading-relaxed">
                          Sesión personalizada para comprender en profundidad las necesidades específicas, 
                          cultura organizacional y expectativas del proceso.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-[#1A2B4A]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-file-list-3-line text-[#1A2B4A] text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-2">Definición del perfil objetivo</h4>
                        <p className="text-gray-600 text-sm leading-relaxed">
                          Elaboración detallada de competencias técnicas, experiencia requerida, 
                          idiomas y características culturales del candidato ideal.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-[#1A2B4A]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-map-pin-line text-[#1A2B4A] text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-2">Análisis del mercado objetivo</h4>
                        <p className="text-gray-600 text-sm leading-relaxed">
                          Estudio de disponibilidad de talento en el país de destino, 
                          condiciones del mercado laboral y expectativas salariales.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-[#1A2B4A]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-calendar-check-line text-[#1A2B4A] text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-2">Planificación del proceso</h4>
                        <p className="text-gray-600 text-sm leading-relaxed">
                          Establecimiento de cronograma, hitos clave y canales de comunicación 
                          para el seguimiento continuo del proceso.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Fase 2 */}
              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-[#1A2B4A] to-[#2A3B5A] px-8 py-6">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center backdrop-blur-sm flex-shrink-0">
                      <span className="text-3xl font-bold text-white">02</span>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-white mb-1">Búsqueda y evaluación</h3>
                      <p className="text-white/80">Identificación y validación de candidatos cualificados</p>
                    </div>
                  </div>
                </div>
                <div className="p-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-[#1A2B4A]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-search-line text-[#1A2B4A] text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-2">Búsqueda activa de talento</h4>
                        <p className="text-gray-600 text-sm leading-relaxed">
                          Identificación de candidatos a través de nuestra red internacional, 
                          base de datos cualificada y canales especializados.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-[#1A2B4A]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-file-search-line text-[#1A2B4A] text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-2">Preselección rigurosa</h4>
                        <p className="text-gray-600 text-sm leading-relaxed">
                          Análisis exhaustivo de perfiles: validación de experiencia, 
                          competencias técnicas, idiomas y disponibilidad de movilidad.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-[#1A2B4A]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-user-search-line text-[#1A2B4A] text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-2">Entrevistas en profundidad</h4>
                        <p className="text-gray-600 text-sm leading-relaxed">
                          Evaluación detallada de motivaciones, expectativas profesionales, 
                          adaptabilidad cultural y alineación con el proyecto.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-[#1A2B4A]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-shield-check-line text-[#1A2B4A] text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-2">Verificación de referencias</h4>
                        <p className="text-gray-600 text-sm leading-relaxed">
                          Validación de trayectoria profesional, logros destacados 
                          y recomendaciones de empleadores anteriores.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Fase 3 */}
              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-[#1A2B4A] to-[#2A3B5A] px-8 py-6">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center backdrop-blur-sm flex-shrink-0">
                      <span className="text-3xl font-bold text-white">03</span>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-white mb-1">Presentación y seguimiento</h3>
                      <p className="text-white/80">Coordinación del proceso de selección final</p>
                    </div>
                  </div>
                </div>
                <div className="p-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-[#1A2B4A]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-presentation-line text-[#1A2B4A] text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-2">Presentación de candidatos</h4>
                        <p className="text-gray-600 text-sm leading-relaxed">
                          Entrega de perfiles seleccionados con informes detallados, 
                          incluyendo evaluaciones, fortalezas y recomendaciones.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-[#1A2B4A]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-calendar-event-line text-[#1A2B4A] text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-2">Coordinación de entrevistas</h4>
                        <p className="text-gray-600 text-sm leading-relaxed">
                          Organización de entrevistas entre empresa y candidatos, 
                          facilitando la comunicación y resolviendo aspectos logísticos.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-[#1A2B4A]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-hand-heart-line text-[#1A2B4A] text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-2">Negociación y oferta</h4>
                        <p className="text-gray-600 text-sm leading-relaxed">
                          Apoyo en la negociación de condiciones laborales, salario, 
                          beneficios y aspectos relacionados con la reubicación.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-[#1A2B4A]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-user-follow-line text-[#1A2B4A] text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-2">Seguimiento post-contratación</h4>
                        <p className="text-gray-600 text-sm leading-relaxed">
                          Acompañamiento durante los primeros meses para asegurar 
                          una integración exitosa y resolver cualquier incidencia.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-12 bg-gradient-to-br from-[#1A2B4A] to-[#2A3B5A] rounded-2xl p-12 text-center">
              <h3 className="text-3xl font-bold text-white mb-4">¿Listo para iniciar su proceso de selección?</h3>
              <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
                Nuestro equipo está preparado para ayudarle a encontrar el talento que su empresa necesita
              </p>
              <button
                onClick={() => setActiveTab('nueva')}
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#1A2B4A] font-bold rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap"
              >
                <i className="ri-add-circle-line text-xl"></i>
                Solicitar reunión estratégica
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}